import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface SectionHeadProps extends Omit<BoxProps, 'title'> {
  /** Section number — rendered zero-padded, e.g. 1 -> "01" */
  index: number | string
  title: React.ReactNode
  /** Right-aligned mono marginalia, hidden below 700px */
  aside?: React.ReactNode
}

const Root = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  gap: 22,
  alignItems: 'baseline',
  borderTop: '1px solid var(--ds-ink)',
  paddingTop: 18,
  '@media (max-width: 700px)': {
    gridTemplateColumns: 'auto 1fr',
  },
})

const No = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 11,
  letterSpacing: '0.14em',
  color: 'var(--ds-ink)',
  fontWeight: 600,
})

const Title = styled('h2')({
  margin: 0,
  fontFamily: 'var(--ds-font)',
  fontSize: 'clamp(28px, 3.6vw, 44px)',
  fontWeight: 740,
  letterSpacing: '-0.04em',
  lineHeight: 1.02,
})

const Aside = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.14em',
  color: 'var(--ds-ink-subtle)',
  textTransform: 'uppercase',
  textAlign: 'right',
  lineHeight: 1.8,
  '@media (max-width: 700px)': { display: 'none' },
})

/**
 * Numbered editorial section header — the "01 — Every pixel survives."
 * scaffold reused across every section of Landing v2.
 */
export function SectionHead({ index, title, aside, ...rest }: SectionHeadProps) {
  const padded = typeof index === 'number' ? String(index).padStart(2, '0') : index
  return (
    <Root {...rest}>
      <No>{padded}</No>
      <Title>{title}</Title>
      {aside && <Aside>{aside}</Aside>}
    </Root>
  )
}
